import { colors } from "@rubi/ui";

import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Add,
  Edit as EditIcon,
  ArrowBack,
  Publish as PublishIcon,
  ArrowDropDown,
  ContentCopy as ContentCopyIcon,
  Compress as CompressIcon,
  Delete as DeleteIcon,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DataGrid, GridColDef, GridSortModel } from "@mui/x-data-grid";
import "./SurveyManagement.scss";

import { useLocalStorage } from "@react-hooks-library/core";
import {
  getOrgSurveyConfigPortal,
  getOrgSurveyVersionOverview,
  SurveyVersionOverview,
} from "@rubi/api-generated";
import {
  deleteSurvey,
  postSurveyCreate,
} from "@rubi/api-generated/clientRs/sdk.gen";
import { toast } from "@rubi/ui";
import { useNavigate, useParams, useLocation } from "react-router";

import type { SurveyExportFormat } from "@/api";
import { SURVEYJS_LOCALE_NAMES } from "@/common/constants/languages";
import { LinkCell } from "@/common/DataGridCells";
import { ILocaleObject } from "@/components/online-reporting/types";
import { AuthStateContext } from "@/contexts/AuthContext";

import { BulkEditModal } from "./BulkEditModal";
import { useFormBuilderContext } from "./FormContextProvider";
import { BulkDeployModal } from "./modals/BulkDeployModal";
import { CondenseAllModal } from "./modals/CondenseAllModal";
import { CopyReportsModal } from "./modals/CopyReportsModal";
import { ExportReportsModal } from "./modals/ExportReportsModal";
import { extractLocaleStringFromField, formatVersionNumber } from "./utils";

/**
 * Recursively extracts all available language codes from a SurveyJS configuration.
 * SurveyJS stores translations as locale objects like { default: "Text", fr: "Texte", es: "Texto" }
 */
const getAvailableLanguages = (
  config: Record<string, unknown> | unknown,
): string[] => {
  const languages = new Set<string>();

  const traverse = (obj: unknown) => {
    if (!obj || typeof obj !== "object") return;

    if (Array.isArray(obj)) {
      obj.forEach((item) => traverse(item));
      return;
    }

    const record = obj as Record<string, unknown>;

    // Check if this is a locale object (has "default" key with a string value)
    if ("default" in record && typeof record.default === "string") {
      // Collect all other keys as language codes
      Object.keys(record).forEach((key) => {
        if (key !== "default" && typeof record[key] === "string") {
          languages.add(key);
        }
      });
    }

    // Continue traversing nested objects
    Object.values(record).forEach((value) => traverse(value));
  };

  traverse(config);
  return Array.from(languages).sort();
};

interface ColumnWidths {
  id: number;
  title: number;
  version: number;
  time_created: number;
  time_deployed: number;
  status: number;
  translations: number;
  actions: number;
}

const useDynamicColumnWidths = (
  topPageRef: React.RefObject<HTMLElement>,
): ColumnWidths => {
  const calculateColumnWidths = useCallback(
    (parentWidth: number): ColumnWidths => {
      const baseWidth = parentWidth
        ? parentWidth * 0.95
        : window.innerWidth * 0.95;

      const ratios = {
        id: 2.5,
        title: 3.5,
        version: 1,
        time_created: 2,
        time_deployed: 2,
        status: 1.5,
        translations: 1.5,
        actions: 0.8,
      };
      const totalRatios = Object.values(ratios).reduce(
        (acc, val) => acc + val,
        0,
      );

      const columnWidths: ColumnWidths = {
        id: (baseWidth / totalRatios) * ratios.id,
        title: (baseWidth / totalRatios) * ratios.title,
        version: (baseWidth / totalRatios) * ratios.version,
        time_created: (baseWidth / totalRatios) * ratios.time_created,
        time_deployed: (baseWidth / totalRatios) * ratios.time_deployed,
        status: (baseWidth / totalRatios) * ratios.status,
        translations: (baseWidth / totalRatios) * ratios.translations,
        actions: (baseWidth / totalRatios) * ratios.actions,
      };

      return columnWidths;
    },
    [],
  );

  return calculateColumnWidths(
    topPageRef.current?.parentElement?.offsetWidth ?? 300,
  );
};

const PERSISTED_SORT_FIELDS = new Set([
  "parent_survey_id",
  "title",
  "time_created",
  "time_deployed",
]);

const SURVEY_SORT_STORAGE_PREFIX = "surveyListSort";

const parseStoredSortModel = (stored: unknown): GridSortModel => {
  if (!Array.isArray(stored)) return [];

  return stored.filter(
    (item): item is GridSortModel[number] =>
      !!item &&
      typeof item === "object" &&
      typeof (item as GridSortModel[number]).field === "string" &&
      PERSISTED_SORT_FIELDS.has((item as GridSortModel[number]).field) &&
      ((item as GridSortModel[number]).sort === "asc" ||
        (item as GridSortModel[number]).sort === "desc"),
  );
};

const SurveyListView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const orgId = params.orgId;

  const {
    surveysWithActiveVersions,
    setSurveysWithActiveVersions,
    surveyConfigs,
    setSurveyConfigs,
  } = useFormBuilderContext();

  const { currentUser } = useContext(AuthStateContext);

  // Scoped to sort surveys per user: localStorage is per-origin, so a shared browser profile
  // would otherwise leak one user's preference to the next.
  const [storedSortModel, setStoredSortModel] = useLocalStorage<GridSortModel>(
    `${SURVEY_SORT_STORAGE_PREFIX}:${currentUser?.id ?? "anon"}`,
    [],
  );

  const sortModel = useMemo(
    () => parseStoredSortModel(storedSortModel),
    [storedSortModel],
  );

  const handleSortModelChange = useCallback(
    (newModel: GridSortModel) => {
      setStoredSortModel(
        newModel.filter((item) => PERSISTED_SORT_FIELDS.has(item.field)),
      );
    },
    [setStoredSortModel],
  );

  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [surveyToDelete, setSurveyToDelete] =
    useState<SurveyVersionOverview | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [bulkDeployModalOpen, setBulkDeployModalOpen] = useState(false);
  const [copyReportsModalOpen, setCopyReportsModalOpen] = useState(false);
  const [condenseAllModalOpen, setCondenseAllModalOpen] = useState(false);
  const [exportReportsModalOpen, setExportReportsModalOpen] = useState(false);
  const [exportFormat, setExportFormat] =
    useState<SurveyExportFormat>("markdown");
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [actionsMenuAnchor, setActionsMenuAnchor] =
    useState<null | HTMLElement>(null);

  const fetchSurveys = useCallback(async () => {
    try {
      const [surveysResponse, configsResponse] = await Promise.all([
        getOrgSurveyVersionOverview({
          path: { org_id: orgId ?? "" },
        }),
        getOrgSurveyConfigPortal({
          path: { org_id: orgId ?? "" },
        }),
      ]);
      setSurveysWithActiveVersions(surveysResponse?.data!.surveys || []);
      setSurveyConfigs(configsResponse?.data!.surveys || []);
    } catch (error) {
      console.error("Error fetching surveys:", error);
      navigate("/");
    }
  }, [orgId, navigate, setSurveysWithActiveVersions, setSurveyConfigs]);

  useEffect(() => {
    fetchSurveys();
  }, [fetchSurveys]);

  // Refresh surveys if returning from successful bulk edit
  useEffect(() => {
    if (location.state?.bulkEditSuccess) {
      fetchSurveys();
      // Clear the state so it doesn't re-trigger on subsequent renders
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, fetchSurveys, navigate, location.pathname]);

  const topPageRef = useRef<HTMLInputElement | null>(null);
  const columnWidths = useDynamicColumnWidths(topPageRef);
  const app = location.pathname.includes("rubiintel")
    ? "rubiintel"
    : "rubireportv2";

  const renderLinkCell = (params) => {
    const buildPath = `/${app}/${orgId}/forms/surveys/${params.row.parent_survey_id}`;
    return (
      <LinkCell
        value={params.value}
        onClick={() =>
          navigate(buildPath, {
            replace: true,
            state: { orgId },
          })
        }
      />
    );
  };

  const createNewSurveyHandler = async () => {
    try {
      const newSurveyResponse = await postSurveyCreate({
        path: { org_id: orgId ?? "" },
        body: {
          configuration: {},
          pdf_configuration: {},
        },
      });

      const newDraft = newSurveyResponse.data!.survey;
      const newSurveyId = newDraft.parent_survey_id;

      // Navigate to edit mode for the newly created survey
      const app = window.location.pathname.includes("rubiintel")
        ? "rubiintel"
        : "rubireportv2";
      navigate(`/${app}/${orgId}/forms/surveys/${newSurveyId}/edit`);
    } catch (error) {
      console.error("Error creating new survey:", error);
      toast.error("Failed to create survey");
    }
  };

  const hasSurveyActiveConfig = (parentSurveyId: string): boolean => {
    return surveyConfigs.some((config) => config.survey_id === parentSurveyId);
  };

  const handleDeleteClick = (survey: SurveyVersionOverview) => {
    setSurveyToDelete(survey);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!surveyToDelete) return;

    setIsDeleting(true);
    try {
      await deleteSurvey({
        path: {
          org_id: orgId ?? "",
          parent_survey_id: surveyToDelete.parent_survey_id,
        },
      });
      toast.success("Survey deleted successfully");
      setDeleteModalOpen(false);
      setSurveyToDelete(null);
      fetchSurveys();
    } catch (error: unknown) {
      console.error("Error deleting survey:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to delete survey";
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSurveyToDelete(null);
  };

  const columns: GridColDef[] = [
    {
      field: "parent_survey_id",
      headerName: "Survey ID",
      width: columnWidths.id,
      flex: 1,
      minWidth: 280,
      renderCell: (params) => renderLinkCell(params),
    },
    {
      field: "title",
      headerName: "Survey Title",
      width: columnWidths.title,
      flex: 1,
      minWidth: 200,
      valueGetter: (_, row) => {
        const version = row.active_version || row.draft_version;
        if (!version) return "Untitled Survey";

        const config = version.configuration;
        const title = config?.title;

        // Handle title that may be a string or a locale object (e.g., { default: "English", fr: "French" })
        if (title) {
          return (
            extractLocaleStringFromField(title as string | ILocaleObject) ||
            "Untitled Survey"
          );
        }
        return "Untitled Survey";
      },
    },
    {
      field: "version",
      headerName: "Version",
      width: columnWidths.version,
      minWidth: 80,
      valueGetter: (_, row) => {
        const version = row.active_version;
        if (!version) return "-";
        return formatVersionNumber(version);
      },
    },
    {
      field: "time_created",
      headerName: "Created At",
      width: columnWidths.time_created,
      flex: 1,
      minWidth: 150,
      valueGetter: (_, row) => {
        const version = row.active_version || row.draft_version;
        return version?.time_created;
      },
      valueFormatter: (value) =>
        value ? new Date(value).toLocaleString() : "N/A",
    },
    {
      field: "time_deployed",
      headerName: "Deployed At",
      width: columnWidths.time_deployed,
      flex: 1,
      minWidth: 150,
      valueGetter: (_, row) => row.active_version?.time_deployed,
      valueFormatter: (value) =>
        value ? new Date(value).toLocaleString() : "-",
    },
    {
      field: "status",
      headerName: "Status",
      width: columnWidths.status,
      minWidth: 150,
      renderCell: ({ row }) => {
        const hasActive = row.active_version;
        const isPublished = row.is_published;
        const hasDraft = row.has_draft;

        const getStatusChip = () => {
          if (!hasActive) {
            return <Chip label="Draft Only" color="warning" size="small" />;
          }
          if (isPublished) {
            return <Chip label="Published" color="success" size="small" />;
          }
          return <Chip label="Active" color="info" size="small" />;
        };

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {getStatusChip()}
            {hasDraft && hasActive && (
              <Chip label="Draft" color="warning" size="small" />
            )}
          </Box>
        );
      },
    },
    {
      field: "translations",
      headerName: "Translations",
      width: columnWidths.translations,
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        const version = row.active_version || row.draft_version;
        const languages = version?.configuration
          ? getAvailableLanguages(version.configuration)
          : [];
        const count = languages.length;

        if (count === 0) {
          return "None";
        }

        const languageNames = languages
          .map(
            (code) =>
              SURVEYJS_LOCALE_NAMES[
                code as keyof typeof SURVEYJS_LOCALE_NAMES
              ] || code,
          )
          .join(", ");

        return (
          <Tooltip title={languageNames} arrow>
            <span style={{ cursor: "pointer" }}>
              {count} Language{count !== 1 ? "s" : ""}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: columnWidths.actions,
      minWidth: 60,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        const hasActiveConfig = hasSurveyActiveConfig(row.parent_survey_id);
        return (
          <Tooltip
            title={
              hasActiveConfig
                ? "Cannot delete: survey has active configuration. Deactivate the configuration first."
                : "Delete survey"
            }
            arrow
          >
            <span>
              <IconButton
                onClick={() => handleDeleteClick(row)}
                disabled={hasActiveConfig}
                color="error"
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  const handleBack = () => {
    navigate(`/${app}/${orgId}/forms`);
  };

  return (
    <div className="survey_list_container">
      <div id="top-page-div" ref={topPageRef}></div>
      <Button onClick={handleBack} startIcon={<ArrowBack />} sx={{ mb: 2 }}>
        Back to Form Management
      </Button>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="h4" gutterBottom>
            Survey Management
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            gap={2}
          >
            <Button
              variant="outlined"
              color="primary"
              endIcon={<ArrowDropDown />}
              onClick={(e) => setActionsMenuAnchor(e.currentTarget)}
            >
              Tools
            </Button>
            <Menu
              anchorEl={actionsMenuAnchor}
              open={Boolean(actionsMenuAnchor)}
              onClose={() => setActionsMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setActionsMenuAnchor(null);
                  setBulkEditModalOpen(true);
                }}
              >
                <EditIcon fontSize="small" sx={{ mr: 1 }} />
                Bulk Edit
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setActionsMenuAnchor(null);
                  setBulkDeployModalOpen(true);
                }}
              >
                <PublishIcon fontSize="small" sx={{ mr: 1 }} />
                Bulk Deploy
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setActionsMenuAnchor(null);
                  setCondenseAllModalOpen(true);
                }}
              >
                <CompressIcon fontSize="small" sx={{ mr: 1 }} />
                Bulk Condense
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setActionsMenuAnchor(null);
                  setCopyReportsModalOpen(true);
                }}
              >
                <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />
                Copy Reports
              </MenuItem>
            </Menu>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<FileDownloadIcon />}
              endIcon={<ArrowDropDown />}
              onClick={(e) => setExportMenuAnchor(e.currentTarget)}
            >
              Export
            </Button>
            <Menu
              anchorEl={exportMenuAnchor}
              open={Boolean(exportMenuAnchor)}
              onClose={() => setExportMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setExportMenuAnchor(null);
                  setExportFormat("markdown");
                  setExportReportsModalOpen(true);
                }}
              >
                Export as Markdown (.md)
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setExportMenuAnchor(null);
                  setExportFormat("pdf");
                  setExportReportsModalOpen(true);
                }}
              >
                Export as PDF (.pdf)
              </MenuItem>
            </Menu>
            <Button
              variant="contained"
              color="primary"
              onClick={createNewSurveyHandler}
              startIcon={<Add />}
            >
              Create Survey
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        Surveys
      </Typography>
      <DataGrid
        rows={surveysWithActiveVersions}
        columns={columns}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        rowSelection={false}
        getRowId={(row) => (row as any).parent_survey_id}
        sx={{
          width: "100%",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: alpha(colors.common.black, 0.04),
          },
          "& .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight)>.MuiDataGrid-cell":
            {
              whiteSpace: "normal",
              wordWrap: "break-word",
              lineHeight: "inherit",
              display: "flex",
              alignItems: "center",
            },
        }}
        autoHeight
        disableColumnResize={false}
      />

      <BulkEditModal
        open={bulkEditModalOpen}
        onClose={() => setBulkEditModalOpen(false)}
        orgId={orgId ?? ""}
        onSuccess={fetchSurveys}
      />

      <BulkDeployModal
        open={bulkDeployModalOpen}
        onClose={() => setBulkDeployModalOpen(false)}
        orgId={orgId ?? ""}
        onDeploySuccess={() => {
          setBulkDeployModalOpen(false);
          fetchSurveys();
        }}
      />

      <CopyReportsModal
        open={copyReportsModalOpen}
        onClose={() => setCopyReportsModalOpen(false)}
        orgId={orgId ?? ""}
        onCopySuccess={fetchSurveys}
      />

      <CondenseAllModal
        open={condenseAllModalOpen}
        onClose={() => setCondenseAllModalOpen(false)}
        orgId={orgId ?? ""}
        onCondenseSuccess={fetchSurveys}
      />

      <ExportReportsModal
        open={exportReportsModalOpen}
        onClose={() => setExportReportsModalOpen(false)}
        surveys={surveysWithActiveVersions}
        format={exportFormat}
      />

      <Dialog open={deleteModalOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Survey?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone.
          </Alert>
          <DialogContentText>
            This will permanently delete the survey and all its versions.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} /> : undefined}
          >
            {isDeleting ? "Deleting..." : "Delete Survey"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SurveyListView;
